import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailBaseEmailCampaign } from '@feathr/blackbox';
import { Alert, Button, ConfirmModal, Modal } from '@feathr/components';

import type { TConfirmVariant } from './MailrailChangeConfirmModal.utils';
import { getModalCopy } from './MailrailChangeConfirmModal.utils';

interface IMailrailChangeConfirmModalProps {
  variant: TConfirmVariant;
  campaign: MailrailBaseEmailCampaign;
  opened: boolean;
  /** Cancel / dismiss — stay on the editor with changes intact. */
  onClose: () => void;
  /** Confirm action for the live-save and republish variants. */
  onConfirm?: () => void;
  /** "Discard changes" action for the unsaved-navigation variant. */
  onDiscard?: () => void;
  /** Embedded SaveCampaignButton rendered as the primary action of the unsaved-navigation variant. */
  saveButton?: ReactNode;
}

function MailrailChangeConfirmModal({
  variant,
  campaign,
  opened,
  onClose,
  onConfirm,
  onDiscard,
  saveButton,
}: Readonly<IMailrailChangeConfirmModalProps>): JSX.Element {
  const { t } = useTranslation();
  const copy = getModalCopy(variant, campaign, t);

  if (variant === 'unsaved-navigation') {
    return (
      <Modal
        description={copy.body}
        onClose={onClose}
        opened={opened}
        rightActions={
          <>
            <Button name={'cancel'} onClick={onClose}>
              {copy.cancelButtonText}
            </Button>
            <Button name={'discard_changes'} onClick={onDiscard}>
              {copy.discardButtonText}
            </Button>
            {saveButton}
          </>
        }
        title={copy.title}
      >
        {copy.alertTitle && (
          <Alert description={copy.alertDescription} title={copy.alertTitle} type={'warning'} />
        )}
      </Modal>
    );
  }

  return (
    <ConfirmModal
      alertDescription={copy.alertDescription}
      alertTitle={copy.alertTitle}
      alertType={'warning'}
      cancelButtonText={copy.cancelButtonText}
      confirmButtonText={copy.confirmButtonText}
      confirmButtonType={'primary'}
      onClose={onClose}
      onConfirm={onConfirm}
      opened={opened}
      t={t}
      title={copy.title}
    />
  );
}

export default observer(MailrailChangeConfirmModal);
